import React from 'react'
import { cn } from '../shared/utils/cn'
import {
  getAvailableUnits,
  getUnitCategoryMetadata,
  getBaseUnitId,
} from '../utils/unitConversionUtils'
import { UnitOverrides } from '../hooks/useMessageUnitOverrides'

interface UnitCategoryDropdownsProps {
  /** Array of unit category IDs that are present in the current sensor configs */
  presentCategories: number[]
  /** Current unit override selections (categoryId -> unitId) */
  unitOverrides: UnitOverrides
  /** Callback when user changes a unit selection */
  onUnitChange: (categoryId: number, unitId: number) => void
  /** Additional CSS class names */
  className?: string
}

/**
 * Compact horizontal row of unit category dropdowns for the message table header.
 * Displays one dropdown per unit category present in sensor configs.
 */
export function UnitCategoryDropdowns({
  presentCategories,
  unitOverrides,
  onUnitChange,
  className,
}: UnitCategoryDropdownsProps): JSX.Element | null {
  if (presentCategories.length === 0) {
    return null
  }

  return (
    <div className={cn('flex flex-wrap items-center gap-3', className)}>
      {presentCategories.map((categoryId) => {
        const metadata = getUnitCategoryMetadata(categoryId)
        const availableUnits = getAvailableUnits(categoryId)
        const baseUnitId = getBaseUnitId(categoryId)
        const selectedUnitId = unitOverrides[categoryId] ?? baseUnitId ?? availableUnits[0]?.unitId

        // Skip categories with only one unit option
        if (availableUnits.length <= 1) {
          return null
        }

        return (
          <div key={categoryId} className="flex items-center gap-1">
            <span className="text-[10px] text-gray-500">
              {metadata?.longName || `Category ${categoryId}`}:
            </span>
            <select
              value={selectedUnitId}
              onChange={(e) => onUnitChange(categoryId, parseInt(e.target.value, 10))}
              className={cn(
                'text-[10px] font-medium',
                'px-1.5 py-0.5 rounded border border-gray-300',
                'bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500',
                'cursor-pointer'
              )}
            >
              {availableUnits.map((unit) => (
                <option key={unit.unitId} value={unit.unitId}>
                  {unit.abbreviation}
                </option>
              ))}
            </select>
          </div>
        )
      })}
    </div>
  )
}
